/**
 * Suchen/Ersetzen im Quelltext-Editor – eigenes Panel im VS-Code-Stil statt
 * `@codemirror/search`s eingebautem (Kontrollkästchen + Textknöpfe, fest oben
 * angedockt über die volle Breite). Die Logik (Suchlauf, Ersetzen, Tastatur)
 * bleibt komplett bei der Bibliothek – nur die Darstellung ist eigen, plus
 * eine Funktion, die es nicht mitbringt: Suchen/Ersetzen NUR im markierten
 * Bereich (der VS-Code-Zielscheiben-Knopf).
 */

import { EditorSelection, RangeSetBuilder, type Extension } from "@codemirror/state";
import {
  Decoration,
  EditorView,
  ViewPlugin,
  keymap,
  runScopeHandlers,
  type DecorationSet,
  type Panel,
  type ViewUpdate,
} from "@codemirror/view";
import {
  SearchQuery,
  closeSearchPanel,
  findNext,
  findPrevious,
  getSearchQuery,
  openSearchPanel,
  replaceAll,
  replaceNext,
  search,
  searchKeymap,
  setSearchQuery,
} from "@codemirror/search";
import { h } from "@/lib/dom";
import { icon, type IconName } from "@/lib/icons";

interface Range {
  from: number;
  to: number;
}

/** Alle Treffer einer Anfrage, optional auf einen Bereich beschränkt. */
function allMatches(view: EditorView, query: SearchQuery, scope: Range | null): Range[] {
  if (!query.valid) return [];
  const cursor = query.getCursor(view.state, scope?.from ?? 0, scope?.to ?? view.state.doc.length);
  const out: Range[] = [];
  for (let n = cursor.next(); !n.done; n = cursor.next()) out.push({ from: n.value.from, to: n.value.to });
  return out;
}

/**
 * Groß-/Kleinschreibung der Fundstelle auf den Ersetzungstext übertragen
 * ("Preserve Case", wie in VS Code) – nur für den einfachen Textmodus
 * (kein Regex, da dort ohnehin gezielt Gruppen wie `$1` benutzt werden).
 */
function applyCase(sample: string, replacement: string): string {
  if (sample === sample.toUpperCase() && sample !== sample.toLowerCase()) return replacement.toUpperCase();
  if (sample[0] && sample[0] === sample[0].toUpperCase() && sample.slice(1) === sample.slice(1).toLowerCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

/**
 * Ersetzungstext für EINEN Treffer. Bei Regex werden `$1`…`$9`/`$&` aus dem
 * Fundstellentext selbst neu ermittelt (die Bibliothek gibt über die
 * öffentliche API keine Gruppen zu den Treffern heraus) – deckt die üblichen
 * Fälle ab, keine benannten Gruppen (`$<name>`).
 */
function computeReplacement(view: EditorView, query: SearchQuery, match: Range, preserveCase: boolean): string {
  const found = view.state.sliceDoc(match.from, match.to);
  if (query.regexp) {
    try {
      const re = new RegExp(query.search, query.caseSensitive ? "u" : "iu");
      const m = re.exec(found);
      if (m) {
        return query.replace.replace(/\$(\$|&|[1-9]\d?)/g, (_, g: string) =>
          g === "$" ? "$" : g === "&" ? m[0] : (m[Number(g)] ?? "")
        );
      }
    } catch {
      /* ungültiger Ausdruck – Ersetzungstext wörtlich verwenden */
    }
    return query.replace;
  }
  return preserveCase ? applyCase(found, query.replace) : query.replace;
}

/** Wie `findNext`/`findPrevious`, nur auf `scope` beschränkt (wrap innerhalb). */
function stepInScope(view: EditorView, query: SearchQuery, scope: Range, dir: 1 | -1): boolean {
  const matches = allMatches(view, query, scope);
  if (!matches.length) return false;
  const sel = view.state.selection.main;
  const exact = matches.findIndex((m) => m.from === sel.from && m.to === sel.to);
  let target: number;
  if (exact !== -1) {
    target = (exact + dir + matches.length) % matches.length;
  } else if (dir === 1) {
    target = matches.findIndex((m) => m.from >= sel.head);
    if (target === -1) target = 0; // nach dem letzten Treffer -> von vorn
  } else {
    target = matches.length - 1; // vor dem ersten Treffer -> von hinten
    for (let i = matches.length - 1; i >= 0; i--) {
      if (matches[i].to <= sel.head) {
        target = i;
        break;
      }
    }
  }
  const next = matches[target];
  view.dispatch({
    selection: EditorSelection.single(next.from, next.to),
    effects: EditorView.scrollIntoView(next.from, { y: "center" }),
  });
  return true;
}

/** Wie `replaceNext`, nur auf `scope` beschränkt. */
function replaceNextInScope(view: EditorView, query: SearchQuery, scope: Range, preserveCase: boolean): boolean {
  const matches = allMatches(view, query, scope);
  if (!matches.length) return false;
  const sel = view.state.selection.main;
  const at = matches.find((m) => m.from === sel.from && m.to === sel.to);
  if (!at) return stepInScope(view, query, scope, 1);
  const text = computeReplacement(view, query, at, preserveCase);
  view.dispatch({
    changes: { from: at.from, to: at.to, insert: text },
    selection: EditorSelection.cursor(at.from + text.length),
    scrollIntoView: true,
  });
  return true;
}

/** Wie `replaceAll`, nur auf `scope` beschränkt. Gibt die Trefferzahl zurück. */
function replaceAllInScope(view: EditorView, query: SearchQuery, scope: Range, preserveCase: boolean): number {
  const matches = allMatches(view, query, scope);
  if (!matches.length) return 0;
  view.dispatch({
    changes: matches.map((m) => ({ from: m.from, to: m.to, insert: computeReplacement(view, query, m, preserveCase) })),
  });
  return matches.length;
}

// ---------------------------------------------------------------------------
// Markierung des Suchbereichs ("Nur in Auswahl") – dezenter Rahmen, solange
// die Funktion aktiv ist, damit sichtbar bleibt, wo Weiter/Ersetzen wirken.
// ---------------------------------------------------------------------------

let activeScope: Range | null = null;
const scopeMark = Decoration.mark({ class: "cm-searchScope" });

function scopeDecos(): DecorationSet {
  if (!activeScope) return Decoration.none;
  const b = new RangeSetBuilder<Decoration>();
  b.add(activeScope.from, activeScope.to, scopeMark);
  return b.finish();
}

const scopeHighlight = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor() {
      this.decorations = scopeDecos();
    }
    update(_u: ViewUpdate) {
      this.decorations = scopeDecos();
    }
  },
  { decorations: (v) => v.decorations }
);

// ---------------------------------------------------------------------------
// Das Panel
// ---------------------------------------------------------------------------

type Toggle = { btn: HTMLButtonElement; value: boolean };

function toggleBtn(label: string, title: string, initial: boolean, onChange: () => void): Toggle {
  const t: Toggle = {
    value: initial,
    btn: h(
      "button",
      { type: "button", class: "cm-vs-toggle" + (initial ? " active" : ""), title },
      label
    ) as HTMLButtonElement,
  };
  t.btn.addEventListener("click", () => {
    t.value = !t.value;
    t.btn.classList.toggle("active", t.value);
    onChange();
  });
  return t;
}

function iconBtn(name: IconName, title: string, onClick: () => void): HTMLButtonElement {
  const btn = h("button", { type: "button", class: "cm-vs-icon-btn", title, html: icon(name, 15) }) as HTMLButtonElement;
  btn.addEventListener("click", onClick);
  return btn;
}

class VsCodeSearchPanel implements Panel {
  dom: HTMLElement;
  top = true;

  private preserveCase = false;
  private scopeRange: Range | null = null;
  private query: SearchQuery;

  private searchField: HTMLInputElement;
  private replaceField: HTMLInputElement;
  private replaceRow: HTMLElement;
  private chevron: HTMLButtonElement;
  private countEl: HTMLElement;
  private statsEl: HTMLElement;
  private scopeToggle: HTMLButtonElement;
  private caseT: Toggle;
  private wordT: Toggle;
  private regexT: Toggle;
  private preserveT: Toggle;

  constructor(private view: EditorView) {
    this.query = getSearchQuery(view.state);

    // Kurze, einzeilige Auswahl (ein Wort/Begriff) direkt als Suchtext
    // übernehmen – wie in VS Code. Größere Blöcke werden NICHT übernommen,
    // die sind eher als Bereich für "Nur in Auswahl" gedacht.
    const sel = view.state.selection.main;
    const selText = sel.empty ? "" : view.state.sliceDoc(sel.from, sel.to);
    const startSearch = !this.query.search && selText && !selText.includes("\n") ? selText : this.query.search;

    this.searchField = h("input", {
      type: "text",
      class: "cm-vs-input",
      placeholder: "Suchen",
      value: startSearch,
      "main-field": "true",
    }) as HTMLInputElement;
    this.replaceField = h("input", {
      type: "text",
      class: "cm-vs-input",
      placeholder: "Ersetzen",
      value: this.query.replace,
    }) as HTMLInputElement;

    this.caseT = toggleBtn("Aa", "Groß-/Kleinschreibung beachten", this.query.caseSensitive, () => this.commit());
    this.wordT = toggleBtn("ab", "Nur ganze Wörter", this.query.wholeWord, () => this.commit());
    this.regexT = toggleBtn(".*", "Regulärer Ausdruck", this.query.regexp, () => this.commit());
    this.preserveT = toggleBtn("Aa", "Groß-/Kleinschreibung beim Ersetzen beibehalten", false, () => {
      this.preserveCase = this.preserveT.value;
    });

    this.countEl = h("span", { class: "cm-vs-count" });
    this.statsEl = h("span", { class: "cm-vs-stats" });

    this.scopeToggle = iconBtn("target", "Nur in Auswahl suchen/ersetzen", () => this.toggleScope());

    this.chevron = h("button", {
      type: "button",
      class: "cm-vs-icon-btn cm-vs-chevron",
      title: "Ersetzen ein-/ausblenden",
      html: icon("chevron-right", 14),
    }) as HTMLButtonElement;
    this.chevron.addEventListener("click", () => this.setExpanded(!this.expanded));

    const searchRow = h(
      "div",
      { class: "cm-vs-row" },
      this.chevron,
      h("div", { class: "cm-vs-field" }, this.searchField, h("div", { class: "cm-vs-inline-toggles" }, this.caseT.btn, this.wordT.btn, this.regexT.btn)),
      this.countEl,
      iconBtn("chevron-up", "Vorheriger Treffer (Shift+Enter)", () => this.step(-1)),
      iconBtn("chevron-down", "Nächster Treffer (Enter)", () => this.step(1)),
      this.scopeToggle,
      this.statsEl,
      iconBtn("x", "Schließen (Esc)", () => closeSearchPanel(this.view))
    );

    this.replaceRow = h(
      "div",
      { class: "cm-vs-row cm-vs-replace-row" },
      h("div", { class: "cm-vs-chevron-spacer" }),
      h("div", { class: "cm-vs-field" }, this.replaceField, h("div", { class: "cm-vs-inline-toggles" }, this.preserveT.btn)),
      iconBtn("redo", "Ersetzen", () => this.replaceOne()),
      iconBtn("list", "Alle ersetzen", () => this.replaceAllNow())
    );

    this.dom = h("div", { class: "cm-vs-search", onkeydown: (e: KeyboardEvent) => this.keydown(e) }, searchRow, this.replaceRow);

    this.searchField.addEventListener("input", () => this.commit());
    this.replaceField.addEventListener("input", () => this.commit());

    this.setExpanded(false);
    if (startSearch && startSearch !== this.query.search) this.commit();
    else this.updateCount();
  }

  private expanded = false;
  private setExpanded(v: boolean): void {
    this.expanded = v;
    this.replaceRow.style.display = v ? "" : "none";
    this.chevron.innerHTML = icon(v ? "chevron-down" : "chevron-right", 14);
  }

  /** Von außen (Cmd+H) – Panel aufklappen und ins Ersetzen-Feld springen. */
  focusReplace(): void {
    this.setExpanded(true);
    this.replaceField.focus();
    this.replaceField.select();
  }

  private toggleScope(): void {
    if (this.scopeRange) {
      this.scopeRange = null;
      activeScope = null;
    } else {
      const sel = this.view.state.selection.main;
      if (sel.empty) return;
      this.scopeRange = { from: sel.from, to: sel.to };
      activeScope = this.scopeRange;
    }
    this.scopeToggle.classList.toggle("active", !!this.scopeRange);
    this.view.dispatch({}); // Scope-Markierung neu zeichnen
    this.updateCount();
  }

  private commit(): void {
    const query = new SearchQuery({
      search: this.searchField.value,
      caseSensitive: this.caseT.value,
      regexp: this.regexT.value,
      wholeWord: this.wordT.value,
      replace: this.replaceField.value,
    });
    if (!query.eq(this.query)) {
      this.query = query;
      this.view.dispatch({ effects: setSearchQuery.of(query) });
    }
    this.updateCount();
  }

  private updateStats(): void {
    const sel = this.view.state.selection.main;
    const range = this.scopeRange ?? (sel.empty ? null : sel);
    if (!range) {
      this.statsEl.textContent = "";
      return;
    }
    const text = this.view.state.sliceDoc(range.from, range.to);
    const words = text.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
    const chars = [...text.replace(/\n/g, "")].length;
    this.statsEl.textContent = `${words} ${words === 1 ? "Wort" : "Wörter"} · ${chars} Zeichen`;
  }

  private updateCount(): void {
    this.updateStats();
    if (!this.searchField.value) {
      this.countEl.textContent = "";
      return;
    }
    const matches = allMatches(this.view, this.query, this.scopeRange);
    if (!matches.length) {
      this.countEl.textContent = "Keine Treffer";
      return;
    }
    const sel = this.view.state.selection.main;
    const idx = matches.findIndex((m) => m.from === sel.from && m.to === sel.to);
    this.countEl.textContent = idx === -1 ? `${matches.length} Treffer` : `${idx + 1} von ${matches.length}`;
  }

  private step(dir: 1 | -1): void {
    this.commit();
    if (this.scopeRange) stepInScope(this.view, this.query, this.scopeRange, dir);
    else (dir === 1 ? findNext : findPrevious)(this.view);
    this.updateCount();
  }

  private replaceOne(): void {
    this.commit();
    if (this.scopeRange) replaceNextInScope(this.view, this.query, this.scopeRange, this.preserveCase);
    else replaceNext(this.view);
    this.updateCount();
  }

  private replaceAllNow(): void {
    this.commit();
    if (this.scopeRange) replaceAllInScope(this.view, this.query, this.scopeRange, this.preserveCase);
    else replaceAll(this.view);
    this.updateCount();
  }

  private keydown(e: KeyboardEvent): void {
    if (runScopeHandlers(this.view, e, "search-panel")) {
      e.preventDefault();
      return;
    }
    if (e.key === "Enter" && e.target === this.searchField) {
      e.preventDefault();
      this.step(e.shiftKey ? -1 : 1);
    } else if (e.key === "Enter" && e.target === this.replaceField) {
      e.preventDefault();
      this.replaceOne();
    }
  }

  update(u: ViewUpdate): void {
    for (const tr of u.transactions) {
      for (const effect of tr.effects) {
        if (effect.is(setSearchQuery) && !effect.value.eq(this.query)) {
          this.query = effect.value;
          this.searchField.value = this.query.search;
          this.replaceField.value = this.query.replace;
        }
      }
    }
    if (u.selectionSet || u.docChanged) this.updateCount();
  }

  mount(): void {
    this.searchField.select();
  }

  destroy(): void {
    activeScope = null;
    panelByView.delete(this.view);
  }
}

/** `createPanel` gibt nur ein `Panel` zurück – für Cmd+H (siehe unten) muss die Instanz selbst auffindbar bleiben. */
const panelByView = new WeakMap<EditorView, VsCodeSearchPanel>();

/** Cmd+H ist in der Bibliothek nicht vorgesehen (nur Mod-f) – öffnet dasselbe Panel, aufgeklappt auf Ersetzen. */
function openReplace(view: EditorView): boolean {
  openSearchPanel(view);
  panelByView.get(view)?.focusReplace();
  return true;
}

export function markdownSearch(): Extension {
  return [
    search({
      top: true,
      createPanel(view) {
        const p = new VsCodeSearchPanel(view);
        panelByView.set(view, p);
        return p;
      },
    }),
    scopeHighlight,
    keymap.of([{ key: "Mod-h", run: openReplace }, ...searchKeymap]),
  ];
}
